import { Box } from '@mui/material'
import { observer } from 'mobx-react'
import React, { useEffect, useRef, useState } from 'react'
import CarouselCustomBtn from '../../../../components/PhotoGalleryDialog/CarouselCustomBtn'
import tableScrollButtonsStyles from '../../../../utils/components/CustomTable/components/TableScrollButtons/styles/tableScrollButtonsStyles'
import { getStyles } from '../../../../utils/utils/commonUtils'
import { useBodyOverflow } from './useBodyOverflow'

type ScrollDirection = 'left' | 'right' | null

const TableScrollButtons = ({ rowsLength }: { rowsLength: number }) => {
  const classes = getStyles(tableScrollButtonsStyles)
  const [scrollDirection, setScrollDirection] = useState<ScrollDirection>(null)
  const [containerVisible, setContainerVisible] = useState<
    'hidden' | 'visible'
  >('hidden')
  const [containerOpacity, setContainerOpacity] = useState(0)
  const [currentButtonPosition, setCurrentButtonPosition] = useState<
    'fixed' | 'absolute'
  >('fixed')
  const [windowSize, setWindowSize] = useState(window.innerWidth)

  const scrollIntervalRef = useRef<NodeJS.Timeout | null>(null)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const tableContainerRef = useRef<HTMLElement | null>(null)

  const setVisibleButtons = () => {
    setContainerOpacity(1)
    setContainerVisible('visible')
  }

  useEffect(() => {
    timeoutRef.current = setTimeout(setVisibleButtons, 600)

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [rowsLength])

  useEffect(() => {
    const handleResize = () => {
      setWindowSize(window.innerWidth)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const updateButtonPosition = () => {
    setCurrentButtonPosition(
      window.innerHeight < document.body.clientHeight ? 'fixed' : 'absolute'
    )
  }

  useEffect(() => {
    if (containerVisible === 'visible') {
      updateButtonPosition()
      timeoutRef.current = setTimeout(updateButtonPosition, 600)
    }

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [containerVisible, rowsLength])

  useEffect(() => {
    const tableContainer = document.getElementById('custom-table-container')
    if (!tableContainer) return

    tableContainerRef.current = tableContainer
    const leftBtn = document.getElementById('table-scroll-left-btn')
    const rightBtn = document.getElementById('table-scroll-right-btn')

    if (!leftBtn || !rightBtn) return

    const updateButtonVisibility = () => {
      const { scrollLeft, scrollWidth, clientWidth } = tableContainer

      // Left button visibility
      if (scrollLeft <= 0) {
        leftBtn.style.opacity = '0'
        leftBtn.style.pointerEvents = 'none'
        leftBtn.style.display = 'none'
      } else {
        leftBtn.style.opacity = '1'
        leftBtn.style.pointerEvents = 'auto'
        leftBtn.style.display = 'block'
      }

      // Right button visibility (-10px buffer)
      if (scrollLeft >= scrollWidth - clientWidth - 10) {
        rightBtn.style.opacity = '0'
        rightBtn.style.pointerEvents = 'none'
      } else {
        rightBtn.style.opacity = '1'
        rightBtn.style.pointerEvents = 'auto'
      }
    }

    // Initial setup
    leftBtn.style.opacity = '0'
    leftBtn.style.display = 'none'
    rightBtn.style.opacity =
      tableContainer.clientWidth >= tableContainer.scrollWidth ? '0' : '1'

    tableContainer.addEventListener('scroll', updateButtonVisibility)

    return () => {
      tableContainer.removeEventListener('scroll', updateButtonVisibility)
    }
  }, [windowSize])

  const bodyOverflow = useBodyOverflow()
  const getScrollbarWidth = () =>
    window.innerWidth - document.documentElement.clientWidth

  useEffect(() => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current)
    }

    if (scrollDirection) {
      scrollIntervalRef.current = setInterval(() => {
        if (!tableContainerRef.current) return
        tableContainerRef.current.scrollLeft +=
          scrollDirection === 'right' ? 2 : -2
      }, 10)
    }

    return () => {
      if (scrollIntervalRef.current) clearInterval(scrollIntervalRef.current)
    }
  }, [scrollDirection])

  const handleMouseOver = (direction: ScrollDirection) => {
    setScrollDirection(direction)
  }

  const handleMouseOut = () => {
    setScrollDirection(null)
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current)
    }
  }

  const buttonStyles = {
    base: {
      //@ts-ignore
      ...classes.btn,
      position: currentButtonPosition,
      transform:
        currentButtonPosition === 'absolute' ? 'translateY(-50%)' : undefined,
    },
    left: {
      //@ts-ignore
      ...classes.leftBtn,
      left:
        //@ts-ignore
        currentButtonPosition === 'absolute' ? '0' : classes.leftBtnPosition,
    },
    right: {
      //@ts-ignore
      ...classes.rightBtn,
      right:
        currentButtonPosition === 'absolute'
          ? '0'
          : bodyOverflow === 'hidden'
          ? '42px'
          : //@ts-ignore
            classes.rightBtnPosition,
      transform:
        currentButtonPosition === 'absolute'
          ? 'translateY(-50%) rotate(180deg)'
          : 'rotate(180deg)',
    },
  }

  return (
    <Box
      sx={{
        //@ts-ignore
        ...classes.container,
        opacity: containerOpacity,
        visibility: containerVisible,
      }}
    >
      <Box
        onMouseOver={() => handleMouseOver('left')}
        onMouseOut={handleMouseOut}
        id='table-scroll-left-btn'
        // @ts-ignore
        sx={{ ...buttonStyles.base, ...buttonStyles.left }}
      >
        <CarouselCustomBtn />
      </Box>
      <Box
        onMouseOver={() => handleMouseOver('right')}
        onMouseOut={handleMouseOut}
        id='table-scroll-right-btn'
        // @ts-ignore
        sx={{ ...buttonStyles.base, ...buttonStyles.right }}
      >
        <CarouselCustomBtn />
      </Box>
    </Box>
  )
}

export default observer(TableScrollButtons)
